import { TreeNodeData } from './model';

export type ReferencedTreeNode = Omit<TreeNodeData, 'children'> & {
  reference_node: TreeNodeData;
  parent_node: TreeNodeData;
  index: number;
};

export class ReferencedTree {
  private _rootNode: TreeNodeData;
  private _nodeMap: Map<number, ReferencedTreeNode>;
  private _cloneNumber = 0;

  get root() {
    return this._rootNode;
  }

  constructor(root: TreeNodeData, clone: number) {
    this._cloneNumber = clone;
    this._rootNode = root;
    this._nodeMap = ReferencedTree.createNodeMap(this._rootNode, null);
  }

  static createNodeMap(
    rootNode: TreeNodeData,
    parentNode: TreeNodeData | null,
    currentMap?: Map<number, ReferencedTreeNode>,
  ) {
    const node_map: Map<number, ReferencedTreeNode> = currentMap ?? new Map();

    if (parentNode) {
      const index = parentNode.children.findIndex((a) => a.id === rootNode.id);

      if (index === -1) {
        throw new Error('Unexpected missing index');
      }

      node_map.set(rootNode.id, {
        reference_node: rootNode,
        ...rootNode,
        parent_node: parentNode,
        index,
      });
    } else {
      node_map.set(rootNode.id, {
        reference_node: rootNode,
        ...rootNode,
        parent_node: rootNode,
        index: -1,
      });
    }

    for (const child_node of rootNode.children) {
      ReferencedTree.createNodeMap(child_node, rootNode, node_map);
    }

    return node_map;
  }

  getNodePathFromId = (id: number) => {
    const path = [id];

    let current_node = this._nodeMap.get(id);

    if (!current_node) {
      return null;
    }

    while (current_node) {
      const parent_node = this._nodeMap.get(current_node.parent_node.id);

      if (!parent_node) {
        return null;
      }

      if (parent_node === current_node) {
        // Reached the root
        break;
      }

      path.unshift(parent_node.id);
      current_node = parent_node;
    }

    return path;
  };

  getIndexInParent = (node_id: number) => {
    const node = this._nodeMap.get(node_id);

    if (!node) {
      throw new Error('Unexpected missing map');
    }

    return {
      parent: node.parent_node,
      index: node.index,
    };
  };

  moveNode = (subject_id: number, target_parent_id: number, index: number) => {
    if (index < 0) {
      throw new Error('Unexpected negative index for move');
    }

    const current_node = this._nodeMap.get(subject_id);

    if (!current_node) {
      throw new Error('Unexpected missing subject node for move');
    }

    if (!current_node.parent_node) {
      throw new Error('Unexpected invalid move of root node');
    }

    const target_parent_node =
      this._nodeMap.get(target_parent_id)?.reference_node;

    if (!target_parent_node) {
      throw new Error('Unexpected missing target parent node for move');
    }

    const current_parent_node = this._nodeMap.get(
      current_node.parent_node.id,
    )?.reference_node;

    if (!current_parent_node) {
      throw new Error(`Unexpected missing subject parent node for move`);
    }

    const splice_remove_idx = current_parent_node.children.findIndex(
      (a) => a.id === current_node.id,
    );

    if (splice_remove_idx === -1) {
      throw new Error('Unexpected missing subject node index in parent');
    }

    current_parent_node.children.splice(splice_remove_idx, 1);
    target_parent_node.children.splice(index, 0, current_node.reference_node);

    this._nodeMap.set(current_node.id, {
      ...current_node,
      parent_node: target_parent_node,
      index,
    });

    return this;
  };

  insertNode = (target_parent_id: number, index: number) => {
    const target_parent_node =
      this._nodeMap.get(target_parent_id)?.reference_node;

    if (!target_parent_node) {
      throw new Error('Unexpected missing target parent node for move');
    }

    target_parent_node.children.splice(index, 0, {
      id: Number(`${new Date().getTime()}.1`),
      text: 'New Node',
      children: [],
    });

    return this;
  };

  deleteNode = (node_id: number) => {
    const idx = this.getIndexInParent(node_id);
    idx.parent.children.splice(idx.index, 1);
    return this;
  };

  setNodeText = (target_id: number, text: string) => {
    const target = this._nodeMap.get(target_id)?.reference_node;

    if (!target) {
      throw new Error('Unexpected missing target node for set text');
    }

    target.text = text;

    return this;
  };

  static clone = (tree: ReferencedTree) => {
    return new ReferencedTree(
      structuredClone(tree.root),
      tree._cloneNumber + 1,
    );
  };
}
